"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import {
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
// NOTE: restrict this key by HTTP referrer in Google Cloud, or move it to an env var.
const GOOGLE_KEY = "AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";

const formatCurrency = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_OPTIONS = [
  { value: "planning", label: "Planning" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
];

const VERSION_OPTIONS = [
  { value: 3, label: "V3 — New System (PDF Budget)" },
  { value: 2, label: "V2 — Assembly Budget" },
];

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  red: {
    wrap: "bg-red-500/10 text-red-400 ring-red-500/20",
    dot: "fill-red-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  blue: {
    wrap: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
    dot: "fill-blue-400",
  },
  orange: {
    wrap: "bg-orange-500/10 text-orange-400 ring-orange-500/20",
    dot: "fill-orange-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", dot = true, children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      {dot && (
        <svg
          viewBox="0 0 6 6"
          aria-hidden="true"
          className={classNames("size-1.5", t.dot)}
        >
          <circle r={3} cx={3} cy={3} />
        </svg>
      )}
      {children}
    </span>
  );
}

const statusTone = (s) =>
  s === "active" ? "green" : s === "completed" ? "blue" : "yellow";

const versionTone = (v) => (v === 3 ? "orange" : v === 2 ? "blue" : "gray");

const capitalize = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : "Planning";

const inputClass =
  "block w-full rounded-lg bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none";

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors disabled:cursor-not-allowed disabled:opacity-40";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#111827",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    minHeight: "2.5rem",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    "input:focus": { boxShadow: "none" },
  }),
  singleValue: (provided) => ({ ...provided, color: "#e5e7eb" }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (provided) => ({ ...provided, color: "#e5e7eb" }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { backgroundColor: "#f97316", color: "#fff" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#6b7280" }),
  clearIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": { color: "#f97316" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#374151" : "#1f2937",
    color: state.isSelected ? "#fb923c" : "#e5e7eb",
    "&:active": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    borderRadius: "0.5rem",
    overflow: "hidden",
    zIndex: 9999,
  }),
};

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">
      {children}
    </label>
  );
}

function FormField({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function Modal({ title, badge, onClose, children, maxWidth = "max-w-lg" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div
        className={classNames(
          "w-full rounded-2xl bg-gray-800 shadow-2xl ring-1 ring-white/10",
          maxWidth,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {badge}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </div>
  );
}

function IconButton({ onClick, title, tone = "gray", children }) {
  const tones = {
    gray: "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
    red: "text-red-400 hover:bg-red-500/10",
    orange: "text-orange-400 hover:bg-orange-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={classNames("rounded-lg p-1.5 transition-colors", tones[tone])}
    >
      {children}
    </button>
  );
}

function SortableTh({ label, active, order, onClick }) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
    >
      <button
        onClick={onClick}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-100"
      >
        {label}
        {active &&
          (order === "desc" ? (
            <ChevronDownIcon className="size-4 text-orange-400" />
          ) : (
            <ChevronUpIcon className="size-4 text-orange-400" />
          ))}
      </button>
    </th>
  );
}

const EMPTY_FORM = {
  projectName: "",
  address: "",
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  latitude: null,
  longitude: null,
  originalContractValue: "",
  accountId: "",
  projectManager: "",
  foreman: "",
  version: 3, // default to V3 for all new projects
};

export default function ProjectsList({ userId }) {
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const saved = sessionStorage.getItem("projectsFilter");
    return saved ? JSON.parse(saved) : ["planning", "active"];
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const navigate = useNavigate();

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });

  const closeAlert = () => setAlert((a) => ({ ...a, open: false }));

  const showAlert = (
    type,
    title,
    message,
    onConfirm = null,
    showCancel = false,
  ) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      showCancel,
      onConfirm: onConfirm || closeAlert,
    });
  };

  // Persist status filter
  useEffect(() => {
    sessionStorage.setItem("projectsFilter", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  // Fetch projects + supporting data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");
        const headers = { Authorization: `Bearer ${token}` };

        const statusQuery =
          selectedStatuses.length > 0
            ? `&status=${selectedStatuses.join(",")}`
            : "";

        const [projectRes, accountRes, usersRes] = await Promise.all([
          axios.get(
            `${API}/projects?page=${currentPage}&limit=${itemsPerPage}${statusQuery}`,
            { headers },
          ),
          axios.get(`${API}/accounts`, { headers }),
          axios.get(`${API}/users`, { headers }),
        ]);

        setProjects(projectRes.data.projects || []);
        setTotalItems(projectRes.data.total || 0);
        setTotalPages(projectRes.data.pages || 1);
        setCurrentPage(projectRes.data.currentPage || currentPage);
        setAccounts(accountRes.data.filter((a) => a.type === "account"));
        setUsers(
          usersRes.data.filter((u) =>
            ["admin", "project_manager", "foreman", "manager"].includes(u.role),
          ),
        );
      } catch (err) {
        const msg = err.response?.data?.message || "Failed to load data.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedStatuses, currentPage]);

  const handleRowClick = (projectId, e) => {
    if (e.target.closest(".actions-column")) return;
    navigate(`/Projects/${projectId}`);
  };

  const handleDeleteConfirm = (projectId) => {
    showAlert(
      "warning",
      "Confirm Delete",
      "Are you sure you want to delete this project? This action cannot be undone.",
      () => handleDelete(projectId),
      true,
    );
  };

  const handleDelete = async (projectId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects((prev) => prev.filter((p) => p._id !== projectId));
      setTotalItems((prev) => prev - 1);
      showAlert("success", "Deleted", "Project deleted successfully.");
    } catch (err) {
      showAlert("error", "Error", err.response?.data?.message || err.message);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "originalContractValue") {
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value))
        setFormData((f) => ({ ...f, [name]: value }));
    } else {
      setFormData((f) => ({ ...f, [name]: value }));
    }
  };

  const handlePlaceSelect = async (place) => {
    if (!place?.value) {
      showAlert("error", "Error", "Please select a valid address.");
      return;
    }
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.value.place_id}&key=${GOOGLE_KEY}`,
      );
      const data = await res.json();
      if (data.status !== "OK" || !data.results.length)
        throw new Error("Geocoding failed");

      const result = data.results[0];
      const { lat, lng } = result.geometry.location;
      let street = "",
        city = "",
        state = "",
        postalCode = "",
        country = "";

      result.address_components.forEach((c) => {
        if (c.types.includes("street_number") || c.types.includes("route"))
          street += (street ? " " : "") + c.long_name;
        if (c.types.includes("locality")) city = c.long_name;
        if (c.types.includes("administrative_area_level_1"))
          state = c.short_name;
        if (c.types.includes("postal_code")) postalCode = c.long_name;
        if (c.types.includes("country")) country = c.long_name;
      });

      setFormData((f) => ({
        ...f,
        address: place.label,
        street,
        city,
        state,
        postalCode,
        country,
        latitude: lat,
        longitude: lng,
      }));
    } catch (err) {
      showAlert(
        "error",
        "Error",
        "Error fetching geocoordinates: " + err.message,
      );
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!formData.projectName)
      return showAlert("error", "Error", "Project Name is required.");
    if (
      !formData.originalContractValue ||
      isNaN(parseFloat(formData.originalContractValue))
    )
      return showAlert(
        "error",
        "Error",
        "Please enter a valid Original Contract Value.",
      );
    if (!formData.latitude || !formData.longitude)
      return showAlert(
        "error",
        "Error",
        "Please select a valid address to set geocoordinates.",
      );
    if (!formData.accountId)
      return showAlert("error", "Error", "Please select an account.");

    try {
      const token = localStorage.getItem("token");
      const payload = {
        projectName: formData.projectName,
        address: {
          formattedAddress: formData.address,
          street: formData.street,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country,
          latitude: formData.latitude,
          longitude: formData.longitude,
        },
        originalContractValue: parseFloat(formData.originalContractValue),
        accountId: formData.accountId,
        projectManager: formData.projectManager || null,
        foreman: formData.foreman || null,
        createdBy: userId,
        version: formData.version,
      };

      const res = await axios.post(`${API}/projects`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProjects((prev) => [res.data, ...prev]);
      setTotalItems((prev) => prev + 1);
      setShowAddModal(false);
      setFormData({ ...EMPTY_FORM });
      showAlert(
        "success",
        "Created",
        `Project created successfully as V${formData.version}!`,
      );
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message ||
          "Failed to create project: " + err.message,
      );
    }
  };

  const handleCancelAdd = () => {
    setShowAddModal(false);
    setFormData({ ...EMPTY_FORM });
  };

  const handleStatusFilterChange = (selected) => {
    setSelectedStatuses(selected ? selected.map((o) => o.value) : []);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    setSortOrder(sortField === field && sortOrder === "asc" ? "desc" : "asc");
    setSortField(field);
  };

  // Sorting applies to the current page only
  const sortedProjects = [...projects].sort((a, b) => {
    if (sortField === "projectName")
      return sortOrder === "asc"
        ? (a.projectName || "").localeCompare(b.projectName || "")
        : (b.projectName || "").localeCompare(a.projectName || "");
    const da = new Date(a.createdAt),
      db = new Date(b.createdAt);
    return sortOrder === "desc" ? db - da : da - db;
  });

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const rangeStart =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);

  const pmUsers = users.filter((u) =>
    ["admin", "project_manager"].includes(u.role),
  );
  const foremanUsers = users.filter((u) =>
    ["foreman", "manager"].includes(u.role),
  );

  return (
    <div className="mx-auto max-w-full px-4 py-6">
      <div className="rounded-2xl bg-gray-900 p-4 shadow-xl ring-1 ring-white/10 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
                Operations
              </p>
              <h1 className="text-2xl font-bold text-white">Projects</h1>
            </div>
            <span className="rounded-full bg-gray-800 px-2.5 py-1 text-xs font-semibold text-gray-300 ring-1 ring-inset ring-white/10">
              {totalItems} total
            </span>
          </div>
          <button onClick={() => setShowAddModal(true)} className={btnPrimary}>
            <PlusIcon className="size-4" />
            Create Project
          </button>
        </div>

        {/* Filter */}
        <div className="mb-6 rounded-xl bg-gray-800 p-4 ring-1 ring-white/10">
          <Label>Filter by Status</Label>
          <Select
            isMulti
            options={STATUS_OPTIONS}
            value={STATUS_OPTIONS.filter((o) =>
              selectedStatuses.includes(o.value),
            )}
            onChange={handleStatusFilterChange}
            placeholder="All statuses"
            className="w-full md:max-w-lg"
            isClearable
            isSearchable={false}
            styles={customSelectStyles}
          />
        </div>

        {error && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-500/20">
            <span>{error}</span>
            <button
              onClick={() => setError("")}
              className="text-red-300 hover:text-red-100"
              aria-label="Dismiss"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
        )}

        {/* Create modal */}
        {showAddModal && (
          <Modal title="Create Project" onClose={handleCancelAdd}>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <FormField label="Project Name *">
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleFormChange}
                  className={inputClass}
                  required
                />
              </FormField>

              <FormField label="Address *">
                <GooglePlacesAutocomplete
                  autocompletionRequest={{
                    componentRestrictions: { country: ["ca"] },
                  }}
                  apiKey={GOOGLE_KEY}
                  selectProps={{
                    onChange: handlePlaceSelect,
                    placeholder: "Enter an address",
                    value: formData.address
                      ? { label: formData.address, value: formData.address }
                      : null,
                    styles: customSelectStyles,
                  }}
                />
              </FormField>

              <FormField label="Account *">
                <select
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleFormChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select an account</option>
                  {accounts.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.companyName}
                    </option>
                  ))}
                </select>
              </FormField>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Project Manager">
                  <select
                    name="projectManager"
                    value={formData.projectManager}
                    onChange={handleFormChange}
                    className={inputClass}
                  >
                    <option value="">Select a Project Manager</option>
                    {pmUsers.map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Foreman">
                  <select
                    name="foreman"
                    value={formData.foreman}
                    onChange={handleFormChange}
                    className={inputClass}
                  >
                    <option value="">Select a Foreman</option>
                    {foremanUsers.map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>

              <FormField label="Original Contract Value *">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                    $
                  </span>
                  <input
                    type="text"
                    name="originalContractValue"
                    value={formData.originalContractValue}
                    onChange={handleFormChange}
                    placeholder="0.00"
                    className={classNames(inputClass, "pl-7")}
                    required
                  />
                </div>
              </FormField>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleCancelAdd}
                  className={btnSecondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={classNames(
                    "inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none focus:ring-2",
                    formData.version === 3
                      ? "bg-orange-500 hover:bg-orange-400 focus:ring-orange-500/50"
                      : "bg-blue-600 hover:bg-blue-500 focus:ring-blue-500/50",
                  )}
                >
                  Create V{formData.version} Project
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Table */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="inline-block size-8 animate-spin rounded-full border-b-2 border-orange-500" />
            <p className="mt-3 text-sm text-gray-400">Loading projects...</p>
          </div>
        ) : sortedProjects.length === 0 ? (
          <div className="rounded-xl bg-gray-800 px-6 py-14 text-center ring-1 ring-white/10">
            <p className="text-sm font-semibold text-gray-200">
              No projects found
            </p>
            <p className="mt-1 text-sm text-gray-400">
              Try changing the status filter, or create a new project.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl bg-gray-800 ring-1 ring-white/10">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-white/10">
                  <thead className="bg-gray-800">
                    <tr>
                      <SortableTh
                        label="Project"
                        active={sortField === "projectName"}
                        order={sortOrder}
                        onClick={() => handleSort("projectName")}
                      />
                      {[
                        "Project #",

                        "Account",
                        "Project Manager",
                        "Foreman",
                        "Status",
                      ].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400"
                        >
                          {h}
                        </th>
                      ))}
                      <SortableTh
                        label="Created"
                        active={sortField === "createdAt"}
                        order={sortOrder}
                        onClick={() => handleSort("createdAt")}
                      />
                      <th
                        scope="col"
                        className="actions-column whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-400"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {sortedProjects.map((project) => {
                      const account =
                        project.accountId?.companyName ||
                        accounts.find((a) => a._id === idOf(project.accountId))
                          ?.companyName;
                      const pmName =
                        project.projectManager?.displayName ||
                        users.find(
                          (u) => u._id === idOf(project.projectManager),
                        )?.displayName;
                      const foremanName =
                        project.foreman?.displayName ||
                        users.find((u) => u._id === idOf(project.foreman))
                          ?.displayName;
                      const version = project.version || 1;

                      return (
                        <tr
                          key={project._id}
                          className="cursor-pointer transition-colors hover:bg-gray-700/50"
                          onClick={(e) => handleRowClick(project._id, e)}
                        >
                          <td className="max-w-xs px-4 py-3">
                            <p className="truncate text-sm font-semibold text-gray-100">
                              {project.projectName}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                              {project.address?.city ||
                                project.address?.formattedAddress ||
                                ""}
                            </p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span className="font-mono text-sm font-medium text-orange-400">
                              {project.projectNumber || "—"}
                            </span>
                          </td>

                          <td
                            className="max-w-[12rem] truncate px-4 py-3 text-sm text-gray-300"
                            title={account}
                          >
                            {account || "Not Assigned"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                            {pmName || "Unassigned"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                            {foremanName || "Unassigned"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <Badge tone={statusTone(project.status)}>
                              {capitalize(project.status)}
                            </Badge>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-300">
                            {project.createdAt
                              ? new Date(project.createdAt).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td className="actions-column whitespace-nowrap px-4 py-3">
                            <div className="flex items-center justify-end gap-0.5">
                              <IconButton
                                title="View"
                                tone="orange"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/Projects/${project._id}`);
                                }}
                              >
                                <EyeIcon className="size-5" />
                              </IconButton>
                              <IconButton
                                title="Delete"
                                tone="red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteConfirm(project._id);
                                }}
                              >
                                <TrashIcon className="size-5" />
                              </IconButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-sm text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-200">
                  {rangeStart}–{rangeEnd}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-200">
                  {totalItems}
                </span>{" "}
                projects
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={btnSecondary}
                >
                  <ChevronLeftIcon className="size-4" />
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={btnSecondary}
                >
                  Next
                  <ChevronRightIcon className="size-4" />
                </button>
              </div>
            </div>
          </>
        )}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={closeAlert}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
